import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Flex,
  Image,
  Text,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
  Button,
  Switch,
  HStack,
  Portal,
} from "@chakra-ui/react";
import { HamburgerIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Home, Youtube, Disc, Zap, MoreHorizontal, HelpCircle, Globe, MessageSquare, CreditCard, Briefcase } from "react-feather";
import { useTranslation } from "react-i18next";
import { LanguagePill, LanguageSheet, useLanguagePreference } from "./LanguageSwitcher";
import LanguageFlag from "./LanguageFlag";
import SpotlightCoachmark from "./guide/SpotlightCoachmark";
import {
  GUIDE_EVENT_SKIPPED, GUIDE_REPLAY_PATH, TEST_TOGGLE_GUIDE,
  isSeenOnce, markSeenOnce, setPendingGuide, takePendingGuide, readForcedGuide,
} from "../lib/guideFlow";
import { useUser } from "../contexts/UserContext";
import useSupportInquiryVisible from "../hooks/useSupportInquiryVisible";
import useOpenchatVisible from "../hooks/useOpenchatVisible";
import usePlatformNews from "../hooks/usePlatformNews";
import { useNavigate, useLocation } from "react-router-dom";
import { getPlatform } from "../lib/platformUtils";
import NotificationPopover from "./NotificationPopover";
import { GUIDE_HOME } from "../lib/guideLinks";
import { logoutToLanding } from "../lib/logout";
import TierNavBadge from "./TierNavBadge";
import useCreatorTierSummary from "../hooks/useCreatorTierSummary";
import useRookiePromotionStatus from "../hooks/useRookiePromotionStatus";
import { withRookieReview } from "../lib/tierCardCopy";
import { isTierEligibleRole } from "../lib/tierEligibility";
import { showsPartnerCenterDoor } from "../lib/partnerNav";
import useAcademyProgress from "../hooks/useAcademyProgress";
import { isAcademyViewer, academyWeekLabel, ACADEMY_ROUTE } from "../lib/academyLessons";
import { tt } from "../i18n/tt";
import useBillingStatus from "../hooks/useBillingStatus";
import { billingBadge as resolveBillingBadge } from "../lib/billingState";
import BillingBadge from "./billing/BillingBadge";
import ImpersonateDrawer, { ImpersonateDrawerButton, ImpersonateViewingBar, revertImpersonation } from "./admin/ImpersonateDrawer";

// 2026-09-01 — 햄버거 드롭다운 안에 있던 등급 카드(`TierDropdownCard`)를 제거했다(사용자 확정).
// 상단바 우측에 `TierNavBadge`가 상시로 나오면서 메뉴 안 카드가 같은 정보를 한 겹 더 보여주게
// 됐기 때문이다. "내 정보"(/settings) 진입 경로는 그 아래 이름/이메일 프로필 블록이 그대로
// 맡는다. 등급 요약을 다시 여기에 넣지 말 것 — 상시 배지(항상 보임) + 홈 카드(첫 화면) +
// 내 등급 화면(상세)로 역할이 이미 나뉘어 있다.

// iOS 만 status bar 만큼 nav 상단 패딩 강제 (env(safe-area-inset-top) 가 0 을 반환하는 케이스 대비 max() fallback).
// Android / Web 은 이전 동작 유지 (별도 패딩 없음).
const TOP_INSET_STYLE = getPlatform() === "ios"
  ? { paddingTop: "max(env(safe-area-inset-top), 47px)" }
  : undefined;

// ≡ 메뉴 묶음 제목 — 「배우기 · 소식 · 도구」 (2026-09-22 시안 H2, docs/design/labely-academy-menu-variants.html)
// MenuItem 이 아니라 라벨이므로 키보드 포커스를 받지 않는다.
const MenuGroupLabel = ({ children }) => (
  <Text px={3} pt={2} pb={1} fontSize="11px" fontWeight="700" color="neutral.400" letterSpacing="0.04em">
    {children}
  </Text>
);

// 라벨은 번역 키다 — 실제 문구는 `src/i18n/locales/*.json` 의 nav.* (다국어, 2026-09-06)
const NAV_ITEMS = [
  { labelKey: "nav.home", path: "/home", icon: Home },
  { labelKey: "nav.channel", path: "/channel", icon: Youtube },
  { labelKey: "nav.album", path: "/album", icon: Disc },
  { labelKey: "nav.inspection", path: "/video-inspection", icon: Zap },
  { labelKey: "nav.revenue", path: "/revenue", icon: Zap },
  { labelKey: "nav.total", path: "/total", icon: MoreHorizontal },
];

const isPathActive = (pathname, path) =>
  pathname === path || pathname.startsWith(path + "/");
// 「내 완성 쇼츠」(/ready-shorts)는 검수 화면 옆 탭이라 「검수」 메뉴가 켜진다(2026-10-07)
const NAV_ALSO_ACTIVE = { "/video-inspection": ["/ready-shorts"] };
const isNavActive = (pathname, path) =>
  isPathActive(pathname, path) || (NAV_ALSO_ACTIVE[path] || []).some((p) => isPathActive(pathname, p));

function TopNavBar() {
  const { userInfo } = useUser();
  // 프로필 메뉴 「멤버십」 옆 상태 배지 — 사이드바와 같은 캐시(useBillingStatus)를 나눠 쓴다
  const billingOverview = useBillingStatus(userInfo?.email);
  const billingBadge = React.useMemo(() => resolveBillingBadge(billingOverview), [billingOverview]);
  // 「문의하기」 노출 — 2026-11-01 0시(KST)부터 · 그 전에는 어드민 「공지」 화면 스위치가 켜졌을 때만(어드민은 미리보기) · 2026-10-23 대표 지시
  const supportOpen = useSupportInquiryVisible(userInfo?.role);
  // 「오픈채팅 입장」 — 2026-11-01 0시(KST)부터 · 어드민은 미리보기 (2026-10-29 · docs/pages/main/OpenChat.md)
  const openchatVisible = useOpenchatVisible();
  // 플랫폼 소식 미열람 점 — 햄버거 「공지·소식」 행. 마운트 1회 조회, 폴링 없음(email 없으면 훅이 아무것도 안 한다).
  const news = usePlatformNews(userInfo?.email, { enabled: isTierEligibleRole(userInfo?.role) });
  const hasUnreadNews = news.rows.some((r) => !news.readMap[r.id]?.read_at);
  const { t } = useTranslation();
  // 모바일 B안 — 햄버거 패널의 「언어」 행이 여는 바텀시트 (docs/design/app-language-switcher-variants.html)
  const [langSheetOpen, setLangSheetOpen] = useState(false);
  const { lang: currentLang } = useLanguagePreference();
  // 2026-09-01 — 상시 등급 배지용 요약. 등급제 비대상 role 은 조회 자체를 건너뛰고(최적화),
  // 최종 판정은 데이터 유무로 다시 한번 한다(summary=null 이면 배지가 렌더되지 않는다).
  // 조회 결과는 훅 안에서 5분간 캐시되므로 홈 카드와 쿼리를 공유한다.
  // 2026-09-03 — 판정을 `isTierEligibleRole`(src/lib/tierEligibility.js)로 교체. 전엔 Admin/권리사가
  // 섞인 role 을 무조건 비대상으로 봐서 "Admin , Creator , …" 인 대표 계정(루키)에 배지가 안 떴다.
  const { summary: tierSummary } = useCreatorTierSummary(userInfo?.email, {
    enabled: isTierEligibleRole(userInfo?.role),
    includeVariants: true,
  });
  // 2026-09-06 — 루키 승급 심사 상태(툴팁 「승급 심사 중 / 보완 필요」). 루키 계열·데뷔 직후에만 조회, 5분 캐시.
  const { status: rookieStatus } = useRookiePromotionStatus(userInfo?.email, {
    enabled: tierSummary?.status === "rookie" || tierSummary?.status === "pre-rookie" || Boolean(tierSummary?.debuted),
  });
  // 배지에 넘길 합친 요약 — 렌더마다 새로 만들면 배지의 뷰모델(useTierView)이 매번 다시 만들어진다(138차 #2)
  const tierMerged = useMemo(() => withRookieReview(tierSummary, rookieStatus), [tierSummary, rookieStatus]);
  // 레이블리 아카데미 — 진입은 이 ≡ 메뉴 「배우기」 묶음뿐이다(대표 지시 2026-09-22: 홈 카드 없음).
  // 스위치(app_config.academy_enabled) 꺼짐·대상 아님이면 훅이 꺼짐 상태를 주고 줄 자체를 그리지 않는다.
  const academy = useAcademyProgress(userInfo?.email, {
    enabled: Boolean(userInfo?.email) && isAcademyViewer(userInfo?.role),
  });
  const academyMenu = useMemo(() => {
    if (!academy.state?.enabled) return null;
    const { progress, state } = academy;
    const cur = progress.current;
    let status;
    if (progress.allDone) status = tt("a.academy.menu.done", "완주 {{v0}}/{{v1}}", { v0: progress.doneCount, v1: progress.total });
    else if (state.openUpto < 0) status = tt("a.academy.menu.soon", "곧 시작");
    else if (cur !== null && cur !== undefined) status = tt("a.academy.menu.status", "{{v0}} · {{v1}}/{{v2}}", { v0: academyWeekLabel(cur), v1: progress.doneCount, v2: progress.total });
    return {
      status,
      // 이번 주 것을 아직 안 봤을 때만 빨간 점 — 다 본 사람에게는 조용하다
      unseen: !progress.allDone && cur !== null && cur !== undefined && !progress.done.includes(cur),
      to: `${ACADEMY_ROUTE}?week=${cur ?? 0}`,
    };
  }, [academy]);
  const navigate = useNavigate();
  const location = useLocation();
  // 운영 서랍 「회원 화면 보기」(2026-10-20 시안 C) — 종전 「다른 사용자 이메일」 입력을 대신한다
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [impersonateMode] = useState(
    !!localStorage.getItem("impersonateEmail")
  );
  const [isActualAdmin] = useState(
    localStorage.getItem("isActualAdmin") === "true"
  );
  // P0-4 — 홈 코치마크를 건너뛰면 메뉴 아래에 「화면 안내 다시 보기」 위치를 3.5초 알려 준다
  const [guideHint, setGuideHint] = useState(null); // { top, right } | null
  useEffect(() => {
    let timer = null;
    const onSkipped = () => {
      const el = document.querySelector('[data-guide="nav.menu"]');
      if (!el) return;
      const r = el.getBoundingClientRect();
      // 2026-09-24: 모달·드로어가 열려 있으면 그 위에 말풍선이 뜨지 않게 이번 힌트는 건너뛴다(다음 기회에 다시)
      if (typeof document !== "undefined" && document.querySelector(".chakra-modal__content")) return;
      setGuideHint({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
      clearTimeout(timer);
      timer = setTimeout(() => setGuideHint(null), 8000); // 버튼이 있으니 읽고 누를 시간을 준다(2026-09-06 QA: 3.5초는 짧고 「버튼이 안 보인다」)
    };
    window.addEventListener(GUIDE_EVENT_SKIPPED, onSkipped);
    return () => { clearTimeout(timer); window.removeEventListener(GUIDE_EVENT_SKIPPED, onSkipped); };
  }, []);

  // 2026-09-27 말풍선 전수조사: 화면을 옮기면 힌트를 접는다 — 8초 동안 다른 화면 위에 그대로 남아 있었다
  useEffect(() => { setGuideHint(null); }, [location.pathname]);
  // 가로 폭·회전이 바뀌면 메뉴 위치가 달라져 힌트 자리가 틀린다 — 접는다(2026-09-27 최종 검수).
  // 세로만 바뀌는 resize(휴대폰 주소창이 접히고 펴짐)는 무시한다 — 상단바가 고정이라 ≡ 자리가 그대로이고, 첫 스크롤에 힌트가 사라지면 읽을 틈이 없다.
  useEffect(() => {
    if (!guideHint) return undefined;
    const width0 = window.innerWidth;
    const onResize = () => { if (window.innerWidth !== width0) setGuideHint(null); };
    const close = () => setGuideHint(null);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", close);
    return () => { window.removeEventListener("resize", onResize); window.removeEventListener("orientationchange", close); };
  }, [guideHint]);

  // P1-4 — 라이브 → 테스트 첫 전환 뒤(새로고침을 거친다) 토글 위에 1단계 안내. ?guide=admin-test-toggle 로 강제
  const [toggleGuideOpen, setToggleGuideOpen] = useState(() => {
    try {
      return takePendingGuide() === TEST_TOGGLE_GUIDE.id || readForcedGuide(window.location.search) === TEST_TOGGLE_GUIDE.id;
    } catch {
      return false;
    }
  });
  const closeToggleGuide = () => { markSeenOnce(TEST_TOGGLE_GUIDE.id); setToggleGuideOpen(false); };

  const [isTestMode, setIsTestMode] = useState(
    localStorage.getItem("isTestMode") === "true"
  );
  // 이메일 직접 입력·존재 확인·열기는 ImpersonateDrawer(openAsUser)로, 「돌아가기」는 revertImpersonation 으로 옮겼다
  const handleRevert = () => revertImpersonation(navigate);

  const handleTestModeToggle = () => {
    const newTestMode = !isTestMode;
    setIsTestMode(newTestMode);
    localStorage.setItem("isTestMode", newTestMode.toString());
    if (newTestMode && !isSeenOnce(TEST_TOGGLE_GUIDE.id)) setPendingGuide(TEST_TOGGLE_GUIDE.id); // 새로고침 뒤 안내(P1-4)
    navigate(0);
  };

  const isDisabled = userInfo?.is_not_contract || userInfo?.grade === "CUT";
  // 파트너 겸직(Creator, Partner) — 크리에이터 화면에서 파트너센터로 가는 문. 파트너센터 상단 「크리에이터 홈 →」와 짝
  // (2026-09-28 대표 결정 「파트너도 크리에이터처럼」, docs/ops/partner-works-browse-plan.md §4-2). 판정은 partnerNav.showsPartnerCenterDoor 한 곳. 비파트너에겐 렌더 없음.
  // is_not_contract·CUT 계정은 다른 메뉴와 같이 문을 내지 않는다(ProtectedRoute 가 /partner 를 /total 로 돌려보내 문 뒤가 막힌다)
  const isPartner = showsPartnerCenterDoor(userInfo?.role) && !isDisabled;

  return (
    <Box
      position="sticky"
      top={0}
      zIndex={1000}
      bg="white"
      borderBottom="1px solid"
      borderColor="neutral.200"
      style={TOP_INSET_STYLE}
    >
      <Flex
        h="52px"
        align="center"
        justify="space-between"
        px={{ base: 4, md: 6 }}
        maxW="100%"
        position="relative"
      >
        {/* Logo */}
        <Flex align="center" gap={2}>
          <Image
            src="/laeebly-icon.png"
            alt={t("topbar.logoAlt")}
            boxSize="28px"
            cursor="pointer"
            onClick={() => navigate(isDisabled ? "/total" : "/home")}
          />
          <Text
            fontWeight="700"
            fontSize="md"
            color="neutral.800"
            letterSpacing="-0.5px"
          >
            {t("topbar.brand")}
          </Text>
        </Flex>

        {/* Desktop horizontal nav — absolute center */}
        <Flex
          display={{ base: "none", md: "flex" }}
          align="center"
          gap={1}
          position="absolute"
          left="50%"
          transform="translateX(-50%)"
        >
          {NAV_ITEMS.map((item) => {
            const isActive = isNavActive(location.pathname, item.path);
            const disabled = isDisabled && !["/total", "/settings"].includes(item.path);
            return (
              <Flex
                key={item.path}
                align="center"
                gap={1.5}
                px={3}
                py={1.5}
                borderRadius="6px"
                cursor={disabled ? "not-allowed" : "pointer"}
                opacity={disabled ? 0.4 : 1}
                color={isActive ? "brand.600" : "neutral.500"}
                fontWeight={isActive ? "600" : "500"}
                fontSize="sm"
                position="relative"
                transition="all 0.15s ease"
                _hover={disabled ? {} : { color: "brand.600", bg: "neutral.50" }}
                _after={isActive ? {
                  content: '""',
                  position: "absolute",
                  bottom: "-9px",
                  left: "0",
                  right: "0",
                  height: "2px",
                  bg: "brand.500",
                  borderRadius: "1px",
                } : {}}
                onClick={disabled ? undefined : () => navigate(item.path)}
              >
                <Text>{t(item.labelKey)}</Text>
              </Flex>
            );
          })}
        </Flex>

        {/* Right side: 등급 배지 + notification + user menu */}
        <Flex align="center" gap={1}>
          {/* 2026-09-01 신규 — 상시 등급 배지(진입점 3안). 홈이 아닌 모든 페이지에서 등급이
              보이는 유일한 자리다. 클릭하면 곧장 /my-tier 로 간다(여기서 카드를 펼치지 않는다). */}
          <TierNavBadge summary={tierMerged} />
          {/* 파트너센터(파트너 겸직만) — PC 채운 버튼(보라 = 파트너 배지 색). 휴대폰은 아래 ≡ 메뉴 항목 */}
          {isPartner && (
            <Button
              size="sm"
              h="30px"
              px={3}
              mr={1}
              borderRadius="8px"
              fontSize="12.5px"
              fontWeight="700"
              bg="purple.600"
              color="white"
              _hover={{ bg: "purple.700" }}
              _active={{ bg: "purple.700" }}
              leftIcon={<Briefcase size={14} />}
              display={{ base: "none", md: "inline-flex" }}
              title={tt("a.partnerCreator.topbarTitle", "파트너센터로 이동 — 코드·링크·성과·파트너 정산")}
              data-track="크리에이터 상단바 파트너센터"
              data-guide="topbar.partner-center"
              onClick={() => navigate("/partner")}
            >
              {t("nav.partnerCenter")}
            </Button>
          )}
          {/* 이용 가이드 — 데스크톱은 아이콘, 모바일은 아래 메뉴 항목(자리가 좁다) */}
          <IconButton
            as="a"
            href={GUIDE_HOME}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("topbar.guide")}
            title={t("topbar.guide")}
            icon={<HelpCircle size={18} />}
            variant="ghost"
            size="sm"
            borderRadius="6px"
            color="neutral.500"
            display={{ base: "none", md: "inline-flex" }}
            _hover={{ bg: "brand.50", color: "brand.700" }}
          />
          {/* 문의하기(티켓, /support) — 데스크톱은 아이콘, 모바일은 아래 메뉴 항목 (2026-09-08 PR-D). 11/1 전·스위치 꺼짐이면 숨김(2026-09-10 · 2026-10-23) */}
          {supportOpen.visible && (
            <IconButton
              aria-label={tt("a.4f1a014e", "문의하기")}
              title={tt("a.4f1a014e", "문의하기")}
              icon={<MessageSquare size={18} />}
              variant="ghost"
              size="sm"
              borderRadius="6px"
              color="neutral.500"
              display={{ base: "none", md: "inline-flex" }}
              _hover={{ bg: "brand.50", color: "brand.700" }}
              onClick={() => navigate("/support")}
            />
          )}
          <NotificationPopover />
          {/* 언어 전환(피씨 A안, 확정 시안) — 알림 종과 메뉴(≡) 사이. 휴대폰에서는 숨고
              아래 메뉴 안 「언어」 행이 맡는다 (docs/design/app-language-switcher-variants.html) */}
          <LanguagePill />
          {/* 메뉴를 열면 「건너뛴 안내」 힌트를 접는다 — 힌트(z 1390)가 메뉴 목록을 가렸다(2026-09-27 최종 검수) */}
          <Menu placement="bottom-end" autoSelect={false} onOpen={() => setGuideHint(null)}>
            <MenuButton
              as={IconButton}
              aria-label={t("topbar.menu")}
              data-guide="nav.menu"
              icon={<HamburgerIcon />}
              variant="ghost"
              size="sm"
              borderRadius="6px"
              color="neutral.500"
              _hover={{ bg: "neutral.50", color: "neutral.800" }}
            />
            <MenuList
              minW="304px"
              borderRadius="8px"
              border="1px solid"
              borderColor="neutral.200"
              boxShadow="0 4px 16px rgba(0,0,0,0.08)"
              p={1}
              bg="white"
              // 2026-09-23 — 항목이 늘어(배우기·소식·도구 묶음, 멤버십, 아카데미) 휴대폰 화면보다 길어졌는데
              // 목록 안에서 스크롤이 안 돼 아래쪽 「관리자」·「로그아웃」에 닿을 수 없었다(대표 실기기 보고).
              // 화면 높이 안에서 목록 자체가 스크롤되게 한다. 바깥(페이지) 스크롤로 새지 않도록 contain.
              maxH="calc(100vh - 96px)"
              overflowY="auto"
              overscrollBehavior="contain"
              // 주소창이 있는 모바일 브라우저에서는 100vh 가 보이는 높이보다 크다 — 지원하는 곳은 dvh 로
              sx={{ WebkitOverflowScrolling: "touch", "@supports (height: 100dvh)": { maxHeight: "calc(100dvh - 96px)" } }}
            >
              {/* User info — 2026-08-30 정정(알파 지시+사용자 역제안, 같은 날 사용자 재정정으로
                  최종 확정): 이름/이메일 블록 전체를 클릭 가능한 단일 버튼으로 바꿔 기존
                  Settings 허브(/settings)로 진입시킨다 — 처음엔 별도 /my-info 신규 페이지로
                  구상했으나, Settings.jsx가 이미 같은 구조(프로필 요약+링크 리스트)라 사용자가
                  신규 라우트 대신 그쪽에 "내 등급" 항목만 얹는 쪽으로 재지시(아래 Settings.jsx
                  변경 참고, 기존 /settings 딥링크는 그대로 유지됨). 아래 있던 "내 등급" MenuItem은
                  제거됐다 — 내 등급 진입은 이제 이 블록을 눌러 들어가는 /settings("내 정보") 안
                  링크로 이동(구분선 위 배치는 유지). 이 메뉴에 별도 "설정" MenuItem도 여전히
                  남아 있어(아래) 같은 /settings로 가는 진입점이 두 개가 됐다 — 이번 작업 범위에서는
                  무접촉으로 남겨두기로 사용자 확정(중복 정리는 별도 검토).
                  **(같은 날 후속 정정, 알파 원문 지시 확인)**: 진입 힌트를 ChevronRightIcon
                  하나로만 뒀었는데, 알파의 원 지시가 "내 정보" 라벨 자체를 명시하라는 것이었음이
                  확인돼 이메일 줄 바로 아래에 작은 브랜드 톤 "내 정보 ›" 텍스트를 추가했다.
                  chevron 아이콘은 라벨과 의미가 겹치긴 하지만(둘 다 "여기 누르면 이동" 신호),
                  Settings.jsx 쪽 리스트 행들도 전부 같은 chevron 관례를 쓰고 있어 그 톤을
                  맞추는 쪽으로 남겨뒀다(판단 위임 사항 — 제거하는 게 더 낫다고 보이면 다음 라운드
                  때 빼도 됨). */}
              <MenuItem
                py={2}
                px={3}
                mb={1}
                borderRadius="4px"
                _hover={{ bg: "neutral.50" }}
                onClick={() => navigate("/settings")}
              >
                <Flex align="center" justify="space-between" w="100%">
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color="neutral.800">
                      {userInfo.name}
                    </Text>
                    <Text fontSize="xs" color="neutral.400">
                      {userInfo.email}
                    </Text>
                    <Text fontSize="xs" color="brand.600" fontWeight="600" mt={0.5}>
                      {t("topbar.myInfo")} ›
                    </Text>
                  </Box>
                  <ChevronRightIcon color="neutral.300" boxSize={4} />
                </Flex>
              </MenuItem>
              <Divider borderColor="neutral.100" />

              {/* 멤버십(멤버십 구독료·결제) — 2026-09-22 대표 결정 「멤버십 메뉴는 권고안대로」: 프로필 블록 바로 아래, PC·휴대폰 공통. 오른쪽에 상태 배지(구독 중·할 일·미납·해지 예약).
                  상단 메뉴 항목으로는 넣지 않는다. 시안 docs/design/billing-sheet-copy-variants.html 「멤버십 메뉴는 어디에 두나」 */}
              <MenuItem
                fontSize="sm"
                fontWeight="500"
                py={2}
                px={3}
                mt={1}
                borderRadius="4px"
                color={isPathActive(location.pathname, "/billing") ? "brand.600" : "neutral.600"}
                bg={isPathActive(location.pathname, "/billing") ? "brand.50" : "transparent"}
                _hover={{ bg: "neutral.50" }}
                onClick={() => navigate("/billing")}
                data-guide="nav.billing"
              >
                <Flex align="center" gap={2} w="100%">
                  <Box as={CreditCard} size={16} color="neutral.500" aria-hidden />
                  <Text as="span">{t("settings.billing")}</Text>
                  <Flex align="center" gap={1.5} ml="auto">
                    <BillingBadge badge={billingBadge} />
                    <ChevronRightIcon color="neutral.300" boxSize={4} />
                  </Flex>
                </Flex>
              </MenuItem>

              {/* 언어(모바일 B안) — 패널 첫 줄. 데스크톱은 상단바 알약(LanguagePill)이 맡으므로 숨긴다.
                  누르면 메뉴가 닫히고 바텀시트가 열린다 (docs/design/app-language-switcher-variants.html) */}
              <MenuItem
                display={{ base: "flex", md: "none" }}
                fontSize="sm"
                fontWeight="500"
                py={2}
                px={3}
                mt={1}
                borderRadius="4px"
                color="neutral.600"
                _hover={{ bg: "neutral.50" }}
                onClick={() => setLangSheetOpen(true)}
              >
                <Flex align="center" gap={2} w="100%">
                  <Box as={Globe} size={16} color="neutral.500" aria-hidden />
                  <Text as="span">{t("lang.label")}</Text>
                  <Flex align="center" gap={1.5} ml="auto">
                    <LanguageFlag lang={currentLang} size={18} />
                    <Text as="span" fontSize="xs" color="neutral.400">{t(`lang.${currentLang}`)}</Text>
                    <ChevronRightIcon color="neutral.300" boxSize={4} />
                  </Flex>
                </Flex>
              </MenuItem>

              {/* Mobile-only nav items */}
              <Box display={{ base: "block", md: "none" }} py={1}>
                {[
                  { labelKey: "nav.home", path: "/home", disabled: isDisabled },
                  { labelKey: "nav.channel", path: "/channel", disabled: isDisabled },
                  { labelKey: "nav.album", path: "/album", disabled: isDisabled },
                  { labelKey: "nav.inspection", path: "/video-inspection", disabled: isDisabled },
                  { labelKey: "nav.revenue", path: "/revenue", disabled: isDisabled },
                  { labelKey: "nav.total", path: "/total", disabled: false },
                ].map((item) => {
                  const isActive = isNavActive(location.pathname, item.path);
                  return (
                    <MenuItem
                      key={item.path}
                      fontSize="sm"
                      fontWeight="500"
                      py={2}
                      px={3}
                      borderRadius="4px"
                      opacity={item.disabled ? 0.4 : 1}
                      color={isActive ? "brand.600" : "neutral.600"}
                      bg={isActive ? "brand.50" : "transparent"}
                      _hover={item.disabled ? {} : { bg: "neutral.50" }}
                      onClick={item.disabled ? undefined : () => navigate(item.path)}
                      cursor={item.disabled ? "not-allowed" : "pointer"}
                    >
                      {t(item.labelKey)}
                    </MenuItem>
                  );
                })}
                {/* 파트너센터(파트너 겸직만) — PC 상단 버튼의 휴대폰 짝 */}
                {isPartner && (
                  <MenuItem
                    fontSize="sm"
                    fontWeight="600"
                    py={2}
                    px={3}
                    borderRadius="4px"
                    color="purple.700"
                    _hover={{ bg: "purple.50" }}
                    onClick={() => navigate("/partner")}
                  >
                    <Flex align="center" gap={2}>
                      <Box as={Briefcase} size={16} aria-hidden />
                      <Text as="span">{t("nav.partnerCenter")}</Text>
                    </Flex>
                  </MenuItem>
                )}
                <Divider my={1} borderColor="neutral.100" />
              </Box>

              {/* 2026-09-22 시안 H2(대표 확정) — 항목이 늘어 「배우기 · 소식 · 도구」 세 묶음으로 나눴다.
                  항목 자체와 순서는 종전 그대로고, 아카데미 한 줄이 「배우기」에 들어왔다.
                  아카데미는 홈 카드 없이 이 메뉴가 주 진입점이다(대표 지시: 홈이 이미 길다). */}
              <Box py={1}>
                <MenuGroupLabel>{t("menu.groupLearn")}</MenuGroupLabel>
                <MenuItem
                  as="a"
                  href={GUIDE_HOME}
                  target="_blank"
                  rel="noopener noreferrer"
                  fontSize="sm"
                  fontWeight="600"
                  py={2}
                  px={3}
                  borderRadius="4px"
                  color="brand.700"
                  _hover={{ bg: "brand.50", textDecoration: "none" }}
                >
                  {t("menu.guide")}
                </MenuItem>
                {/* 레이블리 아카데미 — 스위치 꺼짐·대상(크리에이터·어드민) 아니면 academyMenu 가 null 이라 줄이 없다 */}
                {academyMenu && (
                  <MenuItem
                    fontSize="sm"
                  fontWeight="500"
                  py={2}
                  px={3}
                  borderRadius="4px"
                  _hover={{ bg: "neutral.50" }}
                  color="neutral.600"
                    onClick={() => navigate(academyMenu.to)}
                    data-testid="menu-academy"
                  >
                    <Flex align="center" gap={2} w="100%">
                      {academyMenu.unseen && (
                        <Box w="6px" h="6px" borderRadius="full" bg="status.error" flex="none" aria-hidden="true" />
                      )}
                      <Text as="span">{t("menu.academy")}</Text>
                      {academyMenu.status && (
                        <Text as="span" ml="auto" fontSize="xs" color="neutral.400">{academyMenu.status}</Text>
                      )}
                    </Flex>
                  </MenuItem>
                )}
                {/* P0-4 — 홈 첫 신청 코치마크 재진입(건너뛴 뒤에도). ?guide=first_apply 로 강제 시작한다 */}
                <MenuItem
                  fontSize="sm"
                  fontWeight="500"
                  py={2}
                  px={3}
                  borderRadius="4px"
                  _hover={{ bg: "neutral.50" }}
                  color="neutral.600"
                  onClick={() => navigate(GUIDE_REPLAY_PATH)}
                >
                  {t("menu.replayGuide")}
                </MenuItem>
              </Box>
              <Divider borderColor="neutral.100" />
              <Box py={1}>
                <MenuGroupLabel>{t("menu.groupNews")}</MenuGroupLabel>
                <MenuItem
                  fontSize="sm"
                  fontWeight="500"
                  py={2}
                  px={3}
                  borderRadius="4px"
                  _hover={{ bg: "neutral.50" }}
                  color="neutral.600"
                  onClick={() => navigate("/notice")}
                >
                  {t("menu.notice")}
                  {hasUnreadNews && <Box as="span" ml={2} w="6px" h="6px" borderRadius="full" bg="status.error" display="inline-block" verticalAlign="middle" data-testid="menu-notice-unread-dot" />}
                </MenuItem>
                {/* 문의하기(티켓, /support) — 2026-09-08 PR-D. 11/1 전·스위치 꺼짐이면 숨김(2026-09-10 · 2026-10-23) */}
                {supportOpen.visible && (
                  <MenuItem
                    fontSize="sm"
                  fontWeight="500"
                  py={2}
                  px={3}
                  borderRadius="4px"
                  _hover={{ bg: "neutral.50" }}
                  color="neutral.600"
                    onClick={() => navigate("/support")}
                  >
                    {tt("a.4f1a014e", "문의하기")}
                  </MenuItem>
                )}
                {/* 오픈채팅 입장(/openchat) — 「[공지방] 레이블리 - 유튜브」 오픈채팅 이전 · 11/1 전에는 숨김(어드민 미리보기) · 2026-10-29 */}
                {openchatVisible && (
                  <MenuItem
                    fontSize="sm"
                    fontWeight="500"
                    py={2}
                    px={3}
                    borderRadius="4px"
                    _hover={{ bg: "neutral.50" }}
                    color="neutral.600"
                    onClick={() => navigate("/openchat")}
                    data-testid="menu-openchat"
                  >
                    {tt("a.977457a9", "오픈채팅 입장")}
                  </MenuItem>
                )}
              </Box>
              <Divider borderColor="neutral.100" />
              <Box py={1}>
                <MenuGroupLabel>{t("menu.groupTools")}</MenuGroupLabel>
                <MenuItem
                  fontSize="sm"
                  fontWeight="500"
                  py={2}
                  px={3}
                  borderRadius="4px"
                  _hover={{ bg: "neutral.50" }}
                  color="neutral.600"
                  onClick={() => navigate("/promotions")}
                >
                  {t("menu.promotions")}
                </MenuItem>
                {/* 2026-10-26 유료 프로모션은 번들과 따로 — 운영팀이 등록한 채널만 */}
                <MenuItem
                  fontSize="sm"
                  fontWeight="500"
                  py={2}
                  px={3}
                  borderRadius="4px"
                  _hover={{ bg: "neutral.50" }}
                  color="neutral.600"
                  onClick={() => navigate("/paid-promotions")}
                >
                  {t("menu.paidPromotions")}
                </MenuItem>
                <MenuItem
                  fontSize="sm"
                  fontWeight="500"
                  py={2}
                  px={3}
                  borderRadius="4px"
                  _hover={{ bg: "neutral.50" }}
                  color="neutral.600"
                  onClick={() => navigate("/shopping-tag-helper")}
                >
                  {t("menu.shoppingTag")}
                </MenuItem>
                {userInfo.role?.includes("Admin") && (
                  <>
                    <MenuItem
                      fontSize="sm"
                    fontWeight="500"
                    py={2}
                    px={3}
                    borderRadius="4px"
                    _hover={{ bg: "neutral.50" }}
                    color="neutral.600"
                      onClick={() => navigate("/ppl")}
                    >
                      {t("menu.ppl")}
                    </MenuItem>
                    <MenuItem
                      fontSize="sm"
                    fontWeight="500"
                    py={2}
                    px={3}
                    borderRadius="4px"
                    _hover={{ bg: "neutral.50" }}
                    color="neutral.600"
                      onClick={() => navigate("/video-analysis")}
                    >
                      {t("menu.aiCommerce")}
                    </MenuItem>
                  </>
                )}
              </Box>
              <Divider borderColor="neutral.100" />
              <Box py={1}>
                {userInfo.role?.includes("Admin") && (
                  <MenuItem
                    fontSize="sm"
                    py={2}
                    px={3}
                    borderRadius="4px"
                    color="accent.600"
                    fontWeight="600"
                    _hover={{ bg: "accent.50" }}
                    onClick={() => navigate("/admin")}
                  >
                    {t("menu.admin")}
                  </MenuItem>
                )}
                {userInfo.role?.includes("Copyrights Holder") && (
                  <MenuItem
                    fontSize="sm"
                    py={2}
                    px={3}
                    borderRadius="4px"
                    color="brand.500"
                    fontWeight="600"
                    _hover={{ bg: "brand.50" }}
                    onClick={() => navigate("/video/rights-manager")}
                  >
                    {t("menu.rightsManager")}
                  </MenuItem>
                )}
                {/* 2026-08-30 사용자 확정: 이 "설정" MenuItem은 삭제 — 상단 프로필 블록("내 정보"
                    버튼, /settings로 이동)이 완전히 대체한다. /settings 라우트 자체와 다른
                    진입 경로(Settings.jsx 내부 링크 등)는 무접촉, 그대로 유지. */}
                <MenuItem
                  fontSize="sm"
                  fontWeight="500"
                  py={2}
                  px={3}
                  borderRadius="4px"
                  color="status.error"
                  _hover={{ bg: "red.50" }}
                  onClick={logoutToLanding}
                >
                  {t("menu.logout")}
                </MenuItem>
              </Box>
            </MenuList>
          </Menu>
        </Flex>
      </Flex>

      {/* 언어 바텀시트(모바일 B안) — 메뉴 안 「언어」 행이 연다 */}
      <LanguageSheet isOpen={langSheetOpen} onClose={() => setLangSheetOpen(false)} />

      {/* P0-4 재진입 힌트 — 메뉴 버튼 아래 3.5초 */}
      {guideHint && (
        <Portal>
          <Box
            position="fixed"
            top={`${guideHint.top}px`}
            right={`${guideHint.right}px`}
            // 2026-09-27: 모달(1400) 아래·레이 위젯(1200)·GuideBar(1300) 위 — 힌트가 떠 있는 동안 모달이 열리면 모달이 덮는다(전엔 1460 이라 모달 위에 남았다)
            zIndex={1390}
            bg="neutral.800"
            color="white"
            fontSize="13px"
            fontWeight="500"
            px={3.5}
            py={2.5}
            borderRadius="10px"
            boxShadow="0 12px 30px rgba(0,0,0,0.25)"
            maxW={`calc(100vw - ${guideHint.right + 8}px)`}
            role="status"
            data-guide="nav.replay-hint"
          >
            <Box position="absolute" top="-6px" right="12px" w="12px" h="12px" bg="neutral.800" transform="rotate(45deg)" borderRadius="2px" />
            {/* 휴대폰(360~390px)에서는 한 줄(≈480px)이 화면 밖으로 나가 문장 앞이 잘렸다 — 줄바꿈 허용, 버튼은 줄어들지 않게 */}
            <HStack spacing={3} align="center" flexWrap="wrap">
              <Text as="span" flex="1" minW="180px" lineHeight="1.45">{tt("a.1753d4a8", "건너뛴 안내는 위 ≡ 메뉴 「화면 안내 다시 보기」에 있어요")}</Text>
              <Button
                size="xs"
                h="26px"
                colorScheme="brand"
                borderRadius="6px"
                fontWeight="700"
                onClick={() => { setGuideHint(null); navigate(GUIDE_REPLAY_PATH); }}
              >
                {tt("a.732cc7ce", "지금 다시 보기")}
              </Button>
            </HStack>
          </Box>
        </Portal>
      )}
      {/* P1-4 테스트 모드 첫 전환 안내(어드민, 1단계·비차단) */}
      {toggleGuideOpen && (
        <SpotlightCoachmark
          isOpen
          anchor={TEST_TOGGLE_GUIDE.anchor}
          stepIndex={0}
          stepCount={1}
          badge="테스트 모드"
          title={TEST_TOGGLE_GUIDE.title}
          body={TEST_TOGGLE_GUIDE.body}
          strong={TEST_TOGGLE_GUIDE.strong}
          placement={TEST_TOGGLE_GUIDE.placement}
          blocking={false}
          onSkip={closeToggleGuide}
          primary={{ label: tt("a.3ce81313", "확인"), onClick: closeToggleGuide }}
          secondary={[]}
        />
      )}

      {/* Admin: impersonation & test mode bar */}
      {!impersonateMode && userInfo.role && userInfo.role.toLowerCase().includes("admin") && (
        <Flex
          px={{ base: 4, md: 6 }}
          py={2}
          borderTop="1px solid"
          borderColor="neutral.100"
          bg="neutral.50"
          justify="space-between"
          align="center"
          gap={4}
        >
          {userInfo?.role?.includes("Admin") && (
            <HStack spacing={2} align="center" data-guide="admin.test-toggle">
              <Text fontSize="xs" color="neutral.500">{tt("a.a339f2c7", "라이브")}</Text>
              <Switch
                isChecked={isTestMode}
                onChange={handleTestModeToggle}
                colorScheme="red"
                size="sm"
              />
              <Text fontSize="xs" color="neutral.500">{tt("a.3b6e8490", "테스트")}</Text>
            </HStack>
          )}
          <ImpersonateDrawerButton onClick={() => setViewAsOpen(true)} />
        </Flex>
      )}
      {impersonateMode && isActualAdmin && (
        <ImpersonateViewingBar
          email={localStorage.getItem("impersonateEmail")}
          name={userInfo?.name}
          onOpenDrawer={() => setViewAsOpen(true)}
          onRevert={handleRevert}
        />
      )}
      {((!impersonateMode && !!userInfo?.role?.toLowerCase().includes("admin")) || (impersonateMode && isActualAdmin)) && (
        <ImpersonateDrawer
          isOpen={viewAsOpen}
          onClose={() => setViewAsOpen(false)}
          viewingEmail={impersonateMode && isActualAdmin ? localStorage.getItem("impersonateEmail") : null}
        />
      )}
    </Box>
  );
}

export default React.memo(TopNavBar);
